import { getApi, putApi } from '../../services/apiBase';
import { backgroundTaskNotifications, backgroundTaskNotificationRead } from '../../services/apiURL';
import { getNotificationType } from '../../utils/notifications/notificationTypes';
import {
    FETCH_NOTIFICATIONS_LOADING,
    FETCH_NOTIFICATIONS_SUCCESS,
    FETCH_NOTIFICATIONS_ERROR,
    MARK_NOTIFICATION_READ,
} from './types';

// One bell row per process, showing its latest completed run, keyed by the mapped route so the two
// ISIR task-type strings (ISIRImport + ParseISIRDocument) collapse into a single "ISIR Import" row
// and repeated runs of the same process don't stack. Every collapsed run's id is kept on the visible
// row (groupIds) and the row stays unread while ANY run in the group is unread — so clicking it can
// mark the whole group read and no older unread completion is ever stranded, hidden behind a newer
// one, unread in the DB but uncounted by the badge.
const collapseToLatestPerProcess = (items) => {
    const groups = new Map();
    [...items]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .forEach((n) => {
            const config = getNotificationType(n.taskType);
            if (!config) return; // unknown task type — not a bell notification
            const key = config.route;
            if (!groups.has(key)) {
                // Newest run for this process becomes the visible row.
                groups.set(key, { ...n, groupIds: [n.backgroundTaskId] });
            } else {
                const group = groups.get(key);
                group.groupIds.push(n.backgroundTaskId);
                // Any unread run in the group keeps the collapsed row (and the badge) unread.
                if (!n.isRead) group.isRead = false;
            }
        });
    return [...groups.values()];
};

// Poll thunk: fetch the current user's notifications in one call. The backend wraps the payload as
// Response<T> (list at resp.data.data); we collapse to one row per process and derive the unread
// count from that same collapsed set — a process is unread while any of its runs is — so the badge
// counts exactly the rows the user can act on and stays in step with the list.
// Resolves whether the poll succeeded, which is what lets the caller space out its retries after a
// run of failures; the HTTP error itself is still left to the interceptor.
export const fetchNotifications = () => async (dispatch) => {
    dispatch({ type: FETCH_NOTIFICATIONS_LOADING });
    try {
        const resp = await getApi(backgroundTaskNotifications);
        const data = resp?.data?.data ?? {};
        const items = collapseToLatestPerProcess(data.items ?? []);
        dispatch({
            type: FETCH_NOTIFICATIONS_SUCCESS,
            payload: { items, unreadCount: items.filter((n) => !n.isRead).length },
        });
        return true;
    } catch (err) {
        // The interceptor already surfaces HTTP errors; just drop the loading flag so a failed
        // poll doesn't wedge the bell — the next tick retries.
        dispatch({ type: FETCH_NOTIFICATIONS_ERROR });
        return false;
    }
};

// Mark a whole process group read. Takes the collapsed row so it can PUT every run behind it, not
// just the newest — otherwise an older run stays unread in the DB and is hidden forever behind the
// collapsed row. The local flip is dispatched first so the badge responds immediately and navigation
// never waits on the writes; failed PUTs self-heal on the next poll (the list is re-fetched), so the
// errors are intentionally not surfaced again here.
export const markNotificationRead = (notification) => async (dispatch) => {
    const ids = notification.groupIds ?? [notification.backgroundTaskId];
    dispatch({ type: MARK_NOTIFICATION_READ, payload: notification.backgroundTaskId });
    try {
        await Promise.all(ids.map((id) => putApi(backgroundTaskNotificationRead(id))));
    } catch {
        // Deliberately swallowed — next poll re-fetches authoritative read state.
    }
};
